import { computed, effect, inject, Injectable, type Signal } from '@angular/core';
import type { WebUsuario } from '../models/usuario.model';
import { injectLogoutMutation, injectSessionQuery } from '../queries/session.queries';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IdentityStore {
    private readonly transloco = inject(TranslocoService);
    private readonly primeng = inject(PrimeNG);
    private readonly sessionQuery = injectSessionQuery();
    readonly logoutMutation = injectLogoutMutation();

    // Signals derivados — lo que consume el resto de la app
    readonly currentUser: Signal<WebUsuario | undefined> = computed(() => this.sessionQuery.data());

    readonly isAuthenticated: Signal<boolean> = computed(() => {
        return this.sessionQuery.isSuccess() && !!this.sessionQuery.data();
    });

    readonly isLoading: Signal<boolean> = computed(() => this.sessionQuery.isLoading());

    readonly isPending: Signal<boolean> = computed(() => this.sessionQuery.isPending());

    readonly isFetching: Signal<boolean> = computed(() => this.sessionQuery.isFetching());

    readonly locale: Signal<'es' | 'en'> = computed(() => this.currentUser()?.locale ?? 'es');

    readonly timezone: Signal<string> = computed(() => this.currentUser()?.timezone ?? 'America/Matamoros');

    readonly rolId: Signal<number | undefined> = computed(() => this.currentUser()?.rol?.id);

    readonly authorities: Signal<string[]> = computed(() => this.currentUser()?.authorities ?? []);

    readonly clasificacionId: Signal<number | undefined> = computed(() => this.currentUser()?.rol?.clasificacion?.id);

    constructor() {
        effect(() => {
            const locale = this.locale().toLowerCase();
            if (locale !== this.transloco.getActiveLang()) {
                this.transloco.setActiveLang(locale);
            }
        });

        this.transloco.langChanges$.pipe(switchMap((lang) => this.transloco.selectTranslation(lang))).subscribe((t) => {
            const primeng = Object.entries(t)
                .filter(([key]) => key.startsWith('primeng.'))
                .reduce(
                    (acc, [key, value]) => {
                        const parts = key.replace('primeng.', '').split('.');
                        let current: Record<string, unknown> = acc;
                        parts.forEach((part, i) => {
                            if (i === parts.length - 1) {
                                current[part] = value;
                            } else {
                                current[part] = current[part] ?? {};
                                current = current[part] as Record<string, unknown>;
                            }
                        });
                        return acc;
                    },
                    {} as Record<string, unknown>,
                );

            this.primeng.setTranslation(primeng);
        });
    }

    // Metodos de la sesion
    hasAuthority(authority: string): Signal<boolean> {
        return computed(() => this.authorities().includes(authority));
    }
}
